import React, { forwardRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, TextInputProps, TextStyle, View, ViewStyle, StyleProp } from 'react-native';

import colors from '@packages/core/styles/colors';
import { Label, LabelProps } from './label';
import { Typography } from './typography';
import { Icon } from './icon';
import { useAutoExpandTextInput, useHoverState } from './hooks';

const TEXT_LINE_HEIGHT = 24;
const TEXT_VERTICAL_PADDING = 8;
const CONTAINER_BORDER_WIDTH = 2;

export interface TextareaHelperProps extends TextInputProps {
    buttonElement?: JSX.Element;
    style?: ViewStyle;
    label?: string;
    labelStyle?: StyleProp<TextStyle>;
    labelRightElement?: LabelProps['customRightElement'];
    labelWrapperStyle?: StyleProp<ViewStyle>;
    textInputStyle?: TextStyle;
    errorMessage?: string;
    required?: boolean;
    optional?: boolean;
    controlId?: string;
    containerStyle?: ViewStyle;
    labelWrapperStyle?: StyleProp<ViewStyle>;
    showCharCount?: boolean;
}

export const TextareaHelper = forwardRef<TextInput, TextareaHelperProps>(
    (
        {
            buttonElement,
            style,
            label,
            labelStyle,
            labelRightElement,
            labelWrapperStyle,
            showCharCount,
            required,
            optional,
            textInputStyle,
            errorMessage,
            controlId,
            containerStyle,
            minRows,
            maxRows,
            ...textInputProps
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const { isHovered, hoverEventHandlers } = useHoverState();

        const isDisabled = typeof textInputProps.editable === 'boolean' && !textInputProps.editable;

        const autoExpand = useAutoExpandTextInput({
            minRows,
            maxRows,
            lineHeight: TEXT_LINE_HEIGHT,
            verticalPadding: TEXT_VERTICAL_PADDING * 2,
            verticalBorderWidth: CONTAINER_BORDER_WIDTH * 2,
        });

        const webScrollStyle: TextStyle | false =
            Platform.OS === 'web' &&
            autoExpand.isActive &&
            ({ overflowY: autoExpand.isAtMaximum ? 'auto' : 'hidden' } as TextStyle);

        return (
            <View style={style}>
                {!!label && (
                    <Label
                        style={labelStyle}
                        charCount={
                            showCharCount
                                ? `${(textInputProps.value || '').length}/${textInputProps.maxLength}`
                                : undefined
                        }
                        controlId={controlId}
                        required={required}
                        optional={optional}
                        customRightElement={labelRightElement}
                        wrapperStyle={labelWrapperStyle}
                    >
                        {label}
                    </Label>
                )}

                <View
                    {...hoverEventHandlers}
                    style={[
                        styles.inputContainer,
                        !autoExpand.isActive && styles.fixedHeightContainer,
                        autoExpand.containerStyle,
                        !isDisabled && isHovered && styles.hoveredInput,
                        !isDisabled && isFocused && styles.focusedInput,
                        !isDisabled && !!errorMessage && styles.invalidInput,
                        isDisabled && styles.disabledInput,
                        containerStyle,
                    ]}
                >
                    {!!errorMessage && <Icon style={styles.errorIcon} name="error" color="redOne" />}

                    <TextInput
                        nativeID={controlId}
                        ref={ref}
                        {...textInputProps}
                        multiline
                        onFocus={(e) => {
                            setIsFocused(true);
                            textInputProps.onFocus && textInputProps.onFocus(e);
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            textInputProps.onBlur && textInputProps.onBlur(e);
                        }}
                        onContentSizeChange={(e) => {
                            autoExpand.onContentSizeChange(e);
                            textInputProps.onContentSizeChange && textInputProps.onContentSizeChange(e);
                        }}
                        underlineColorAndroid="transparent"
                        style={[styles.textInput, textInputStyle]}
                        placeholderTextColor={colors.textInputPlaceHolder}
                    />

                    {buttonElement}

                    {!!errorMessage && <Icon style={styles.errorIcon} name="error" color="redOne" />}
                </View>

                {!!errorMessage && (
                    <Typography variant="caption" color="redOne" style={styles.errorMessage}>
                        {errorMessage}
                    </Typography>
                )}
            </View>
        );
    }
);

const styles = StyleSheet.create({
    fixedHeightContainer: {
        height: Platform.select({ web: 142, default: 234 }),
    },
    inputContainer: {
        borderRadius: 3,
        borderWidth: 1,
        borderColor: colors.grayFive,
    },
    textInput: {
        flex: 1,
        paddingVertical: TEXT_VERTICAL_PADDING,
        paddingHorizontal: 12,
        fontSize: 17,
        lineHeight: TEXT_LINE_HEIGHT,
        color: colors.textDarkPrimary,
        textAlignVertical: 'top',
    },
    hoveredInput: {
        shadowColor: colors.black,
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.16,
        shadowRadius: 4,
    },
    disabledInput: {
        borderColor: colors.grayTwo,
        color: colors.textDarkSecondary,
        backgroundColor: colors.grayTwo,
    },
    focusedInput: {
        borderWidth: CONTAINER_BORDER_WIDTH,
        borderColor: colors.blueOne,
    },
    invalidInput: {
        borderWidth: CONTAINER_BORDER_WIDTH,
        borderColor: colors.redOne,
    },
    errorIcon: {
        position: 'absolute',
        right: 12,
        top: 10,
    },
    errorMessage: {
        marginTop: 8,
    },
});
